function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import * as React from 'react';
import isEmpty from 'lodash/isEmpty';
import sumBy from 'lodash/sumBy';
import { useEffect, useMemo, useRef, useState } from 'react';
import { withStyles } from 'common/util/react';
import { BarVizReact } from 'common/charts/visualization/bar-viz';
import { BarClickEvent, BarSegment, BarValueType, ColumnWidths, Segment, TooltipDataCallback } from 'common/charts/visualization/bar-viz/types';
import { InsightsQueryResults, SegmentationQueryLimits } from 'common/report/insights/util/types';
import { AnalysisType, ChartPlotStyle, ChartType, InsightsBookmarkParams, SavedLegend, ValueRepresentationType } from 'common/types/reports/bookmark';
import { SegmentLegendHeader } from 'common/charts/misc/segment-legend-header';
import { BarChartTooltipStat, computeBarChartTooltipSections, computeMetricClauseByKey, MetricClauseData } from 'common/report/insights/util/tooltip';
import { getDisplayOptions, getSeriesDisplayHeaders, getSortConfig, shouldShowSeriesTotal } from 'common/report/insights/util';
import { getOffsetParent } from 'common/util/dom';
import { REPORT_LIMITS, ReportName, RESULTS_OVERALL_SUM_KEY } from 'common/report/constants';
import { OTHER_SEGMENT_VALUE } from 'common/report/insights/util/constants';
import { ChartTooltipProps } from 'common/components/chart-tooltip';
import { ContextMenuReact } from 'common/widgets/context-menu';
import { dataGroupStore } from 'common/report/stores/data-groups';
import { getInsightsAggregateCsvExportLimit, getProjectId, getWorkspaceId, isPublicMode } from 'common/report/util';
import { DataGroup } from 'common/widgets/data-group-selector/types';
import { renamePropertyValue } from 'common/report/util/properties';
import { EmptyDataReact } from 'common/charts/misc/empty-data';
import { sortSumTree } from 'common/report/util/sorting/utils';
import { formatMetricNumberWithUnits } from 'common/util';
import { isCustomCsvLimitEnabled } from 'common/widgets/csv-export-modal/utils';
import { eventPropertyDefinitionsStore } from 'common/report/stores/property-definitions';
import { ClauseType } from 'common/report/insights/models/types';
import { UnorderedSumTree } from 'common/report/util/sorting/types';
import { IronContextRegistry } from 'common/report/context-registry';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { useTranslation } from 'common/i18n/hooks';
import { MetricsResource } from 'common/report/model-resources/metrics';
import { SegmentFilterController } from 'common/charts/contexts/segment-filter-controller';
import { LocalizationStore } from 'common/i18n/store';
import { getFinalDisplayOptions, getMetricFormattingDefaultsFromShowClause, metricFormatterFunctionForShowClause } from '../util';
import type { InsightsBarChartProps, LegendData, SegmentExtras } from './types';
import { filterToSelectedSegments, getLegendData, getSumTree, sumTreeToBarVizSegments } from './util';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
const getBarSortConfig = (_ref: {
  bookmarkParams: InsightsBookmarkParams;
  results: InsightsQueryResults;
}) => {
  let {
    bookmarkParams,
    results
  }: {
    bookmarkParams: InsightsBookmarkParams;
    results: InsightsQueryResults;
  } = _ref;
  return getSortConfig({
    bookmarkSortConfig: bookmarkParams.sorting,
    liftComparison: bookmarkParams.liftComparison,
    headerCount: results.headers.length
  })[ChartType.Bar];
};
const isStacked = (bookmarkParams: InsightsBookmarkParams) => {
  return getDisplayOptions(bookmarkParams.sections, bookmarkParams.displayOptions).plotStyle === ChartPlotStyle.Stacked;
};
const getDataGroups = (dataGroups: Record<string, DataGroup>): Record<string, DataGroup> => {
  var _dataGroupStore$value;
  if (dataGroups) {
    return dataGroups;
  }
  return (_dataGroupStore$value = dataGroupStore.value) !== null && _dataGroupStore$value !== void 0 ? _dataGroupStore$value : {};
};
const getTooltipDisplay = (_ref2: {
  bookmarkParams: InsightsBookmarkParams;
  dataGroups: Record<string, DataGroup>;
  metrics: MetricsResource;
  i18n: LocalizationStore;
  results: InsightsQueryResults;
  segment: BarSegment<SegmentExtras>;
}): ChartTooltipProps => {
  let {
    bookmarkParams,
    dataGroups,
    metrics,
    i18n,
    results,
    segment
  }: {
    bookmarkParams: InsightsBookmarkParams;
    dataGroups: Record<string, DataGroup>;
    metrics: MetricsResource;
    i18n: LocalizationStore;
    results: InsightsQueryResults;
    segment: BarSegment<SegmentExtras>;
  } = _ref2;
  const metricClauseByKey = computeMetricClauseByKey({
    sectionsMetadata: results.meta.report_sections,
    bookmarkParams,
    dataGroups: Object.values(getDataGroups(dataGroups))
  });
  const {
    path,
    value,
    secondaryValue,
    timeComparisonValue,
    secondaryTimeComparisonValue,
    liftComparisonValue,
    percentage,
    timeComparisonPercentage,
    baselineComparisonUnit,
    baselineComparisonValue
  } = segment.extras;
  const metricClauseData: MetricClauseData = metricClauseByKey[path[0]];
  const titleColor = segment.color;
  let timeComparisonStat: BarChartTooltipStat;
  let liftComparisonStat: BarChartTooltipStat;
  let baselineComparisonStat: BarChartTooltipStat;
  const stat: BarChartTooltipStat = {
    count: value,
    secondaryCount: secondaryValue,
    dateRange: {
      fromDate: results.date_range.from_date,
      toDate: results.date_range.to_date
    },
    percent: percentage
  };
  if (results.lift_comparison) {
    var _bookmarkParams$liftC;
    liftComparisonStat = {
      count: value,
      dateRange: {
        fromDate: results.date_range.from_date,
        toDate: results.date_range.to_date
      },
      percent: liftComparisonValue * 100 || 0,
      unit: [...segment.extras.path].pop(),
      liftType: (_bookmarkParams$liftC = bookmarkParams.liftComparison) === null || _bookmarkParams$liftC === void 0 ? void 0 : _bookmarkParams$liftC.type
    };
    if (baselineComparisonUnit) {
      baselineComparisonStat = {
        count: baselineComparisonValue,
        dateRange: {
          fromDate: results.date_range.from_date,
          toDate: results.date_range.to_date
        },
        percent: 0,
        //we do not use the percent for baseline
        unit: baselineComparisonUnit === RESULTS_OVERALL_SUM_KEY ? "Overall" : baselineComparisonUnit
      };
    }
  }
  if (results.time_comparison) {
    timeComparisonStat = {
      count: timeComparisonValue,
      secondaryCount: secondaryTimeComparisonValue,
      dateRange: {
        fromDate: results.time_comparison.date_range.from_date,
        toDate: results.time_comparison.date_range.to_date
      },
      percent: timeComparisonPercentage
    };
  }
  const renamedPath = path.map((key, idx) => {
    var _bookmarkParams$secti;
    return renamePropertyValue(key, results.headers[idx], (_bookmarkParams$secti = bookmarkParams.sections) === null || _bookmarkParams$secti === void 0 ? void 0 : _bookmarkParams$secti.group[idx - 1]);
  });
  return _objectSpread(_objectSpread({}, results.time_comparison ? {} : {
    titleColor
  }), {}, {
    sections: computeBarChartTooltipSections({
      formatter: v => {
        const showClause = metricClauseData.clauseType === ClauseType.Show ? metricClauseData : null;
        const formattedValue = metricFormatterFunctionForShowClause(showClause)(v);
        const defaultOptions = getMetricFormattingDefaultsFromShowClause(showClause);
        return formatMetricNumberWithUnits(formattedValue, getFinalDisplayOptions(showClause, eventPropertyDefinitionsStore, metrics, defaultOptions, {
          // Tooltip values should never be abbreviated.
          abbrev: false
        }));
      },
      metricClauseData,
      segments: renamedPath.slice(1),
      stat,
      timeComparisonStat,
      liftComparisonStat,
      baselineComparisonStat,
      titleColor,
      i18n
    })
  });
};

/**
 * on each render:
 * 1. filter and merge segments in the sumTree based on what is selected in the legend
 * 2. create a tooltipDataCallback that uses the transformed sumTree to display accurate data
 */
const getDisplayData = (_ref3: {
  bookmarkParams: InsightsBookmarkParams;
  dataGroups: Record<string, DataGroup>;
  i18n: LocalizationStore;
  legendData: LegendData;
  metrics: MetricsResource;
  results: InsightsQueryResults;
  segmentFilterController: SegmentFilterController;
  sumTree: UnorderedSumTree;
}): {
  segments: Array<Segment<SegmentExtras>>;
  tooltipDataCallback: TooltipDataCallback;
} => {
  let {
    bookmarkParams,
    dataGroups,
    i18n,
    legendData,
    metrics,
    results,
    segmentFilterController,
    sumTree
  }: {
    bookmarkParams: InsightsBookmarkParams;
    dataGroups: Record<string, DataGroup>;
    i18n: LocalizationStore;
    legendData: LegendData;
    metrics: MetricsResource;
    results: InsightsQueryResults;
    segmentFilterController: SegmentFilterController;
    sumTree: UnorderedSumTree;
  } = _ref3;
  const tree = filterToSelectedSegments(sumTree, segmentFilterController);
  const sortConfig = getBarSortConfig({
    bookmarkParams,
    results
  });
  const sortedTree = sortSumTree(tree, sortConfig, true);
  const tooltipDataCallback = (_segment, _bar, barSegment) => getTooltipDisplay({
    bookmarkParams,
    dataGroups,
    metrics,
    i18n,
    results,
    segment: barSegment
  });
  let valueRepresentationType: ValueRepresentationType = getDisplayOptions(bookmarkParams.sections, bookmarkParams.displayOptions).value;

  // TODO(lift) remove this check once we've successfully taken out the abosolute/relative toggle and have bookmark migration in place
  if (results.lift_comparison) {
    valueRepresentationType = ValueRepresentationType.Absolute;
  }
  const canShowSumOfSelected = shouldShowSeriesTotal(bookmarkParams.sections);
  const metricClauseByKey = computeMetricClauseByKey({
    sectionsMetadata: results.meta.report_sections,
    bookmarkParams,
    dataGroups: Object.values(getDataGroups(dataGroups))
  });
  const isStackedMode = isStacked(bookmarkParams);
  const segments = sumTreeToBarVizSegments({
    tree: sortedTree,
    // First entry is null to pad out the event name column
    groupClauses: [null, ...bookmarkParams.sections.group],
    headers: getSeriesDisplayHeaders(bookmarkParams.sections, results.headers, getDataGroups(dataGroups), i18n),
    resultHeaders: results.headers,
    leafSortedKeyMap: legendData.sortedKeyMaps[0],
    isStacked: isStackedMode,
    isTimeComparison: !!results.time_comparison,
    isLiftComparison: !!results.lift_comparison,
    parentValue: sumBy(sortedTree, "value"),
    // used for % of total when multiple events w/ no breakdown
    parentTimeComparisonValue: sumBy(sortedTree, "timeComparisonValue"),
    // used for % of total when multiple events w/ no breakdown
    valueRepresentationType,
    showTotal: canShowSumOfSelected,
    segmentFilterController,
    formatter: (path, value) => {
      const metricClauseData: MetricClauseData = metricClauseByKey[path[0]];
      const showClause = (metricClauseData === null || metricClauseData === void 0 ? void 0 : metricClauseData.clauseType) === ClauseType.Show ? metricClauseData : null;
      const formattedValue = metricFormatterFunctionForShowClause(showClause)(value);
      const defaultOptions = getMetricFormattingDefaultsFromShowClause(showClause);
      const formatted = formatMetricNumberWithUnits(formattedValue, getFinalDisplayOptions(showClause, eventPropertyDefinitionsStore, metrics, defaultOptions));
      return formatted;
    }
  });
  return {
    segments,
    tooltipDataCallback
  };
};
export const InsightsBarChart: React.FC<Pick<IronContextRegistry, `currentProject` | `i18n` | `metrics`> & InsightsBarChartProps> = props => {
  const {
    currentProject,
    dataGroups,
    paramsAndResults,
    segmentFilterController
  } = props;
  const {
    results,
    params: bookmarkParams
  } = paramsAndResults !== null && paramsAndResults !== void 0 ? paramsAndResults : {};
  const [popoverStyles, setPopoverStyles] = useState<{
    top: string;
    left: string;
  } | null>(null);
  const [popoverSegment, setPopoverSegment] = useState<BarSegment<SegmentExtras> | null>(null);
  const [forceUpdateState, forceUpdate] = useState(0);
  const container = useRef(null);
  const getCurrentProject = () => {
    return isPublicMode() ? undefined : currentProject.get().value;
  };
  const updatePersistedLegendSelections = () => {
    var _bookmarkParams$legen;
    const filteredBreakdownSegments = bookmarkParams === null || bookmarkParams === void 0 || (_bookmarkParams$legen = bookmarkParams.legend) === null || _bookmarkParams$legen === void 0 ? void 0 : _bookmarkParams$legen.filteredBreakdownSegments;
    if (filteredBreakdownSegments) {
      const savedLegend: SavedLegend[] = Array.isArray(filteredBreakdownSegments) ? filteredBreakdownSegments : [filteredBreakdownSegments];
      segmentFilterController.loadFromSavedLegend(savedLegend);
    }
  };
  const updateSegmentFilterController = (legendData?: LegendData, queryLimits?: SegmentationQueryLimits) => {
    if (!legendData || !segmentFilterController) {
      return;
    }
    segmentFilterController.loadSections(legendData.sections, {
      defaultNumSelections: REPORT_LIMITS[ReportName.Insights].segments[ChartType.Bar].default,
      maxNumSelections: REPORT_LIMITS[ReportName.Insights].segments[ChartType.Bar].max
    });
    segmentFilterController.segmentationQueryLimit = queryLimits === null || queryLimits === void 0 ? void 0 : queryLimits.limit;
    segmentFilterController.exportSegmentationQueryLimit = isCustomCsvLimitEnabled(getCurrentProject()) ? getInsightsAggregateCsvExportLimit(getCurrentProject()) : REPORT_LIMITS[ReportName.Insights].csvExport;
  };
  const chartData = useMemo(() => {
    if (!(bookmarkParams && results)) {
      return {};
    }
    const sumTree = getSumTree(results, bookmarkParams);
    const sortConfig = getBarSortConfig({
      bookmarkParams,
      results
    });
    const {
      meta
    } = results;
    const {
      apiResultsTruncated,
      queryLimits
    } = meta;
    const legendData = getLegendData({
      apiResultsTruncated,
      groupClauses: bookmarkParams.sections.group,
      headers: getSeriesDisplayHeaders(bookmarkParams.sections, results.headers, getDataGroups(dataGroups), props.i18n),
      resultHeaders: results.headers,
      sortConfig,
      sumTree
    });
    return {
      legendData,
      queryLimits,
      sumTree
    };
  }, [props.dataGroups, props.paramsAndResults]);
  useEffect(() => {
    const {
      legendData,
      queryLimits
    } = chartData;
    const segmentFilterControllerSubscriber = () => {
      forceUpdate(forceUpdateState + 1);
    };
    segmentFilterController.subscribe(segmentFilterControllerSubscriber);
    updateSegmentFilterController(legendData, queryLimits);
    if (!props.dataGroups) {
      dataGroupStore.fetch({
        projectId: getProjectId(),
        workspaceId: getWorkspaceId()
      });
    }
    return () => {
      segmentFilterController.unsubscribe(segmentFilterControllerSubscriber);
    };
  }, []);
  useEffect(() => {
    updatePersistedLegendSelections();
  }, [paramsAndResults]);
  useEffect(() => {
    updateSegmentFilterController();
  }, [paramsAndResults, dataGroups]);
  const handleColumnWidthChange = (columnWidths: ColumnWidths) => {
    var _props$onColumnWidthC;
    props === null || props === void 0 || (_props$onColumnWidthC = props.onColumnWidthChange) === null || _props$onColumnWidthC === void 0 || _props$onColumnWidthC.call(props, columnWidths);
  };
  const handleBarClick = (ev: BarClickEvent) => {
    const {
      pageX,
      pageY,
      barSegment
    } = ev;
    const {
      path
    } = barSegment.extras;

    // don't support actions for "Other"
    if (path[path.length - 1] === OTHER_SEGMENT_VALUE || !(props !== null && props !== void 0 && props.barPopoverCallback)) {
      return;
    }

    // getOffsetParent could return null in some cases
    const parentRect = getOffsetParent(container.current).getBoundingClientRect();
    const gap = 12;
    const left = pageX - parentRect.x - window.scrollX + gap;
    const top = pageY - parentRect.y - window.scrollY + gap;
    setPopoverSegment(barSegment);
    setPopoverStyles({
      top: "".concat(top, "px"),
      left: "".concat(left, "px")
    });
  };
  const handlePopoverClose = () => {
    setPopoverSegment(null);
    setPopoverStyles(null);
  };
  const handlePopoverAction = ev => {
    var _props$onBarPopoverAc;
    // revive `all` from API results which is expected by insights
    // TODO: potentially remove this once the new bar chart is GA
    const {
      color
    } = popoverSegment;
    const {
      path,
      isTimeComparisonSegment
    } = popoverSegment.extras;
    props === null || props === void 0 || (_props$onBarPopoverAc = props.onBarPopoverAction) === null || _props$onBarPopoverAc === void 0 || _props$onBarPopoverAc.call(props, {
      segment: path,
      action: ev.detail.action,
      isTimeComparisonSegment,
      color
    });
    handlePopoverClose();
  };
  const getBarValueType = (): BarValueType => {
    var _bookmarkParams$displ;
    switch (bookmarkParams === null || bookmarkParams === void 0 || (_bookmarkParams$displ = bookmarkParams.displayOptions) === null || _bookmarkParams$displ === void 0 ? void 0 : _bookmarkParams$displ.analysis) {
      case AnalysisType.Linear:
        return BarValueType.Absolute;
      case AnalysisType.Logarithmic:
        return BarValueType.Log;
      default:
        return BarValueType.Absolute;
    }
  };
  const renderPopover = () => {
    var _props$barPopoverCall;
    if (!popoverSegment || !(props !== null && props !== void 0 && props.barPopoverCallback)) {
      return <></>;
    }
    const segment = popoverSegment.extras.path[popoverSegment.extras.path.length - 1];
    return <ContextMenuReact className={styles.popover} style={popoverStyles} {...{
      menuItems: props === null || props === void 0 || (_props$barPopoverCall = props.barPopoverCallback) === null || _props$barPopoverCall === void 0 ? void 0 : _props$barPopoverCall.call(props, segment, [...popoverSegment.extras.path, "all"], {
        isTimeComparisonSegment: popoverSegment.extras.isTimeComparisonSegment
      }),
      onAction: (ev: CustomEvent) => handlePopoverAction(ev),
      onClose: () => handlePopoverClose(),
      open: true,
      position: {
        left: 0,
        top: 0
      },
      relativePositioning: true
    }} data-sentry-element="ContextMenuReact" data-sentry-component="renderPopover" data-sentry-source-file="index.tsx" />;
  };
  const renderInner = () => {
    var _bookmarkParams$colum;
    if (results && isEmpty(results === null || results === void 0 ? void 0 : results.series)) {
      return <EmptyDataReact />;
    }
    const {
      legendData,
      sumTree
    } = chartData;
    if (!sumTree) {
      return null;
    }

    // Memoize the data for BarViz so that it doesn't re-render on every update
    // and cause issues while resizing.
    const {
      segments,
      tooltipDataCallback
    } = useMemo(() => getDisplayData({
      bookmarkParams,
      dataGroups,
      i18n: props.i18n,
      legendData,
      metrics: props.metrics,
      results,
      segmentFilterController,
      sumTree
    }), [bookmarkParams, dataGroups, forceUpdateState, props.i18n, legendData, props.metrics, results, segmentFilterController, sumTree]);
    const barVizSortConfig = props.enableSort ? getBarSortConfig({
      bookmarkParams,
      results
    }) : null;
    return <>
        {segments.length ? <SegmentLegendHeader segmentFilterController={props.segmentFilterController} /> : null}
        <BarVizReact barValueType={getBarValueType()} chartData={segments} columnWidths={bookmarkParams === null || bookmarkParams === void 0 || (_bookmarkParams$colum = bookmarkParams.columnWidths) === null || _bookmarkParams$colum === void 0 ? void 0 : _bookmarkParams$colum.bar} downloadMode={props.downloadMode} enableBarClickEvents={!!(props !== null && props !== void 0 && props.barPopoverCallback)} hideTooltip={!!popoverSegment} onBarClick={ev => handleBarClick(ev)} onColumnWidthChange={columnWidths => handleColumnWidthChange(columnWidths)} onSortingChange={sortConfig => {
        var _props$onSortingChang;
        return props === null || props === void 0 || (_props$onSortingChang = props.onSortingChange) === null || _props$onSortingChang === void 0 ? void 0 : _props$onSortingChang.call(props, sortConfig);
      }} showSegmentLabels={isStacked(bookmarkParams)} isStacked={isStacked(bookmarkParams)} sortConfig={barVizSortConfig} selectedBarSegment={popoverSegment} segmentFilterController={props.segmentFilterController} tooltipDataCallback={tooltipDataCallback} data-sentry-element="BarVizReact" data-sentry-source-file="index.tsx" />
        {renderPopover()}
      </>;
  };
  return <div className={styles.root} elref={props.elref} ref={container} data-sentry-component="InsightsBarChart" data-sentry-source-file="index.tsx">
      {renderInner()}
    </div>;
};
export const InsightsBarChartReact = withStyles((props: InsightsBarChartProps) => {
  const currentProject = useCurrentProject();
  const i18n = useTranslation();
  const metrics = useMetrics();
  return <InsightsBarChart currentProject={currentProject} i18n={i18n} metrics={metrics} {...props} />;
}, css);